"use client";

import { Flower2, Leaf, Sun, Moon, Sparkles, Coffee, Tag, Users, Calendar, Bath, BookOpen, Heart, Star, Gift, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ReferralBenefits() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const categories = [{
    title: "Partner Advantages",
    benefits: [{
      icon: Flower2,
      name: "Priority Deal Review",
      description: "Fast-track underwriting for referred real estate investors."
    }, {
      icon: Bath,
      name: "Direct Access to Capital",
      description: "Professional-grade capital funding for first mortgages and gap equity."
    }, {
      icon: Wind,
      name: "Transparent Fee Structures",
      description: "Clear, competitive referral compensation paid upon closing."
    }, {
      icon: Sparkles,
      name: "Dedicated Account Manager",
      description: "A single point of contact for all your referral deal flow."
    }]
  }, {
    title: "Strategic Support",
    benefits: [{
      icon: Coffee,
      name: "Marketing Collateral",
      description: "Co-branded materials to help you present our funding solutions."
    }, {
      icon: Tag,
      name: "Deal Structuring Assistance",
      description: "Expert guidance on complex capital stacks for your clients."
    }, {
      icon: Heart,
      name: "Quarterly Market Insights",
      description: "Exclusive data on lending trends and real estate capital flows."
    }, {
      icon: Leaf,
      name: "Customized Loan Programs",
      description: "Tailored financing options for your specific investor niche."
    }, {
      icon: Moon,
      name: "Digital Partner Portal",
      description: "Real-time tracking of your referrals and commission status."
    }]
  }, {
    title: "Network Growth",
    benefits: [{
      icon: Gift,
      name: "Referral Bonus Program",
      description: "Tiered incentives for high-volume and repeat referral partners."
    }, {
      icon: Calendar,
      name: "Early Access Events",
      description: "Invitations to private investor roundtables and networking mixers."
    }, {
      icon: Users,
      name: "Partner Masterminds",
      description: "Collaborative sessions with top-tier developers and lenders."
    }, {
      icon: BookOpen,
      name: "Educational Resources",
      description: "Deep dives into private lending, BRRRR, and construction finance."
    }, {
      icon: Star,
      name: "Preferred Partner Status",
      description: "Featured placement in our network of trusted service providers."
    }]
  }];
  return <section data-section-id="158" id="referral-benefits" className="bg-slate-950 py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs tracking-[0.3em] uppercase text-yellow-500 mb-6">
            Partner Incentives
          </p>
          <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[52px] leading-tight text-white">
            Architecting <em className="italic text-yellow-500">Growth.</em>
          </h2>
          <div className="w-12 h-px bg-slate-700 mx-auto mt-8" />
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24 overflow-hidden rounded-lg shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_blueprint_architecture.webp" alt="Architectural blueprint of a financial vault structure with gold accents" loading="lazy" decoding="async" className="w-full h-[400px] lg:h-[720px] object-cover" />
            </div>
          </div>

          {}
          <div ref={contentRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-14">
              {categories.map((category, catIndex) => <div data-index={catIndex} key={catIndex}>
                  {catIndex > 0 && <div className="w-full h-px bg-slate-800 mb-14" aria-hidden="true" />}

                  <h3 className="font-default font-semibold text-2xl text-white mb-8 italic">
                    {category.title}
                  </h3>

                  <ul className="space-y-6">
                    {category.benefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return <li data-index={i} key={i} className="group flex items-start gap-5 cursor-default">
                          <div className="shrink-0 mt-1">
                            <Icon className="w-5 h-5 text-yellow-500 transition-all duration-500 ease-out group-hover:scale-125" strokeWidth={1.25} aria-hidden="true" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-default font-semibold text-lg text-white transition-colors duration-500 group-hover:text-yellow-500">
                              {benefit.name}
                            </h4>
                            <p className="font-default text-sm text-slate-400 mt-1 leading-relaxed">
                              {benefit.description}
                            </p>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>

            {}
            <div className="mt-16 pt-10 border-t border-slate-800">
              <p className="font-default text-xl italic text-slate-500 leading-relaxed">
                "Capital is the foundation of every deal. We provide the structural integrity your referrals need to close with confidence."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}